import {BasicEntity, BasicProperty} from "../../basic-entity-back/annotations";
import {PropertyType} from "../../basic-entity-back/property-type/property-type";
import {ManagerCachingStrategy, PropertyMap} from "../../basic-entity-back/basic-entity-interface/mapping-external";
import {DireccionPersona} from "./direccion-persona.model";
import {TelefonoPersona} from "./telefono-persona.model";
import {NestedBehavior} from "../../basic-entity-back/property-type/nested-model-type";
import {IRI_PROPERTY, TYPE_PROPERTY} from "../../basic-entity-back/basic-entity-interface/mapping-internal";
import {Persona} from "./persona.model";
import {PartialSearchFilter} from "../../basic-entity-back/filters/search-filter";
import {BooleanFilter} from "../../basic-entity-back/filters/boolean-filter";

/**
 * Clínica con identidad propia que cuelga de un cliente con permiso para gestionar asociados
 * (`Cliente.esPadre`). No entra en el sistema y no se le factura: la factura sale siempre al
 * cliente padre, aunque el pedido se entregue en la dirección de la clínica.
 *
 * Portado de cnc-admin (mismo modelo, misma API). Adaptado a este proyecto: el `DireccionPersona`
 * de cnc-client no tiene la propiedad `personaId` (sólo existe en cnc-admin como campo de filtrado),
 * así que no hace falta excluirla al declarar la colección anidada.
 *
 * Extiende `Persona` (no `Resource` directo), igual que hace la API: `ClienteAsociado extends
 * PersonaSuperclass` en el backend. `DireccionPersona.persona`/`TelefonoPersona.persona` se
 * declaran como `PropertyType.Uri(Persona)`, y `InterfaceProviderService.allAcceptedInterfaces()`
 * calcula los tipos aceptados recorriendo la cadena de prototipos (`isInstanceOf`), no por una
 * lista explícita. Con `extends Resource`, `ClienteAsociado` quedaba fuera de esa cadena: al
 * deserializar cualquier `DireccionPersona` cuyo propietario fuera una clínica, `UriCaster.toModel()`
 * no encontraba su `@type` entre los tipos aceptados y lanzaba una excepción sin capturar dentro del
 * `subscribe()` de `CachedInputComponent.loadData()` -sin manejador de error-, así que la suscripción
 * moría en silencio y `options` se quedaba vacío para siempre: el desplegable de dirección de entrega
 * nunca recibía nada en cuanto una de las direcciones a listar pertenecía a una clínica. Reproducido
 * en local: `UriCaster for uriPersona: Invalid uri object with @type ClienteAsociado`.
 *
 * `Persona` no obliga a ningún método nuevo (sólo campos con getter/setter) y el deserializador
 * ignora en silencio cualquier propiedad ausente en la respuesta de la API
 * (`BasicEntitySerialiser.fromApi()`: `if (value !== undefined)` antes de castear), así que heredar
 * los campos de `Persona` que la API no expone para `ClienteAsociado` (nombre, apellidos, fechas,
 * usuario, idioma...) no rompe nada; y al no llevar `tag`, tampoco aparecen solos en ningún
 * formulario -mismo motivo por el que `Persona._direcciones` no aparece en el de `Cliente`-.
 */
@BasicEntity({
    name: 'Clientes asociados',
    endpoint: 'cliente_asociados',
    isPaginated: true,
    paginationSizes: [20, 30, 50],
    modelNameProperty: 'denominacionSocial',
    acceptsBulkIriSearch: true,
    type: 'ClienteAsociado',
    managerCaching: ManagerCachingStrategy.NoCache,
    // El ID no lo ve el cliente en la tabla; Idioma (heredado de Persona) no lo admite la API en asociados
    sinFiltroEnElPanel: ['id', 'idioma']
})
export class ClienteAsociado extends Persona {

    // Sin campo ni get/set propios para 'id': hereda los de Persona (idéntico
    // PropertyType.Integer(), y de paso gana el ExactSearchFilter que ya tenía Persona.id).

    // Circular con cliente.model.ts (Cliente referencia ClienteAsociado y viceversa): se resuelve
    // con FutureUri aquí y el parcheo de este mapa al final de cliente.model.ts, igual que
    // DescuentoCliente.MAP_CLIENTE.
    public static readonly CLIENTE_MAP: PropertyMap = {
        name: 'Cliente',
        type: PropertyType.FutureUri('cliente.model.ts')
    };
    @BasicProperty(ClienteAsociado.CLIENTE_MAP)
    private _cliente;

    // 'sortable: true': la API sí declara OrderFilter sobre 'denominacionSocial' para este recurso
    // (es el único campo que lo lleva, junto al 'order' por defecto del propio ApiResource -ver
    // ClienteAsociado.php-), así que la cabecera de la tabla de asociados.component.ts puede ser
    // pulsable de verdad.
    @BasicProperty({
        name: 'Nombre de la clínica',
        type: PropertyType.String(),
        sortable: true,
        order: 0,
        filters: [PartialSearchFilter]
    })
    private _denominacionSocial: string;

    // Campo y get/set propios para 'cifNif'/'email' (antes se heredaban de Persona sin más): la API expone los dos para
    // ClienteAsociado (grupo 'clientes_asociados' en PersonaSuperclass.php) y les admite «contiene» (SearchFilter
    // partial, ver ClienteAsociado.php), que usa el panel de filtros; no admite OrderFilter (solo 'denominacionSocial').
    // Heredar el 'sortable'/'searchable' de Persona -que sí aplican para Cliente/Empleado/Proveedor- dejaba cabeceras
    // pulsables que no ordenaban nada y un buscador general que la API ignoraba (devolvía la colección entera). Mismo
    // tipo (String()/Email()) y misma nullabilidad que en Persona, sin 'searchable'/'sortable'.
    @BasicProperty({
        name: 'CIF/NIF',
        type: PropertyType.String(),
        nullable: true,
        filters: [PartialSearchFilter]
    })
    protected _cifNif;

    @BasicProperty({
        name: 'Email',
        type: PropertyType.Email(),
        nullable: false,
        filters: [PartialSearchFilter]
    })
    protected _email;

    @BasicProperty({
        name: 'Baja',
        type: PropertyType.Boolean(),
        order: 3,
        filters: [BooleanFilter]
    })
    private _baja: boolean = false;

    // Escribible anidada, igual que Persona.direcciones: el alta/edición del asociado manda
    // siempre la colección completa (nunca un PUT parcial que la omita), que es lo que contiene el
    // riesgo del orphanRemoval de PersonaSuperclass::$direcciones.
    //
    // Sin 'tag' a propósito: esta propiedad no se expone directamente en ningún formulario (igual
    // que Persona._direcciones para Cliente, que tampoco aparece en su ficha). Trae siempre la
    // colección completa -históricas incluidas, que DireccionPersonaSubscriber genera al editar una
    // dirección para no alterar pedidos ya emitidos-. Lo único que ve y edita el usuario es
    // 'direccionesVigentes' más abajo. Que 'columns' se calcule a partir del mapa completo de la
    // API implica que no basta con no tener tag: los componentes que abren el diálogo del asociado
    // también tienen que excluir 'direcciones' de las 'columns' que pasan, o volvería a aparecer
    // como campo suelto.
    // `protected` en el campo (aquí y en Persona): redeclara con exclusiones distintas
    // ([IRI_PROPERTY, TYPE_PROPERTY]) las mismas que ya traía antes de heredar de Persona; con
    // `private` en Persona, TypeScript no deja redeclararla en la subclase.
    @BasicProperty({
        name: 'Direcciones',
        type: PropertyType.NestedModel(DireccionPersona, 'uriPersona', NestedBehavior.CreateOnly, [IRI_PROPERTY, TYPE_PROPERTY]),
        array: true
    })
    protected _direcciones = [];

    // Propiedad derivada, sin dato propio: es sólo un ancla para el decorador (igual que
    // Cliente._direccionFacturacion/_direccionEnvio), la lógica real vive en el getter/setter
    // 'direccionesVigentes' de más abajo.
    @BasicProperty({
        name: 'Direcciones',
        tag: 'Direcciones',
        type: PropertyType.NestedModel(DireccionPersona, 'uriPersona', NestedBehavior.CreateOnly, [IRI_PROPERTY, TYPE_PROPERTY]),
        array: true,
        dontReadFromApi: true
    })
    private _direccionesVigentes;

    // A diferencia de direcciones, sí es escribible anidado: el asociado no ofrece picking de
    // teléfonos existentes (CreateOnly) y no arrastra el mismo riesgo de borrado en cascada.
    // `protected` en el campo (aquí y en Persona): redeclara para añadir 'tag' -Persona.telefonos
    // no lo lleva-, y con `private` en Persona no se puede redeclarar en la subclase.
    @BasicProperty({
        name: 'Teléfonos',
        tag: 'Teléfonos',
        type: PropertyType.NestedModel(TelefonoPersona, 'persona', NestedBehavior.CreateOnly),
        array: true
    })
    protected _telefonos;

    get cliente() {
        return this._cliente;
    }

    set cliente(value) {
        this._cliente = value;
    }

    get denominacionSocial() {
        return this._denominacionSocial;
    }

    set denominacionSocial(value) {
        this._denominacionSocial = value;
    }

    get cifNif() {
        return this._cifNif;
    }

    set cifNif(value) {
        this._cifNif = value;
    }

    get email() {
        return this._email;
    }

    set email(value) {
        this._email = value;
    }

    get baja() {
        return this._baja;
    }

    set baja(value) {
        this._baja = value;
    }

    get direcciones() {
        return this._direcciones;
    }

    set direcciones(value) {
        this._direcciones = value;
    }

    /**
     * Sólo las direcciones vigentes (no históricas) de 'direcciones'. Es lo único que el formulario
     * llega a ver: lo que el usuario añade, edita o borra aquí sólo toca este subconjunto.
     */
    get direccionesVigentes() {
        return (this.direcciones || []).filter(d => d && !d.historico);
    }

    /**
     * Reconstruye 'direcciones' conservando las históricas tal cual estaban -nunca se editan ni se
     * borran, siguen referenciadas por pedidos ya emitidos- y sustituyendo el bloque de vigentes por
     * lo que haya dejado el formulario. Es 'direcciones' (la colección completa), no esto, lo que
     * viaja a la API al guardar.
     */
    set direccionesVigentes(value) {
        this._direccionesVigentes = value;
        this._direcciones = [
            ...(this._direcciones || []).filter(d => d && d.historico),
            ...(value || [])
        ];
    }

    get telefonos() {
        return this._telefonos;
    }

    set telefonos(value) {
        this._telefonos = value;
    }
}
